
import React, { useState } from 'react';

interface ConciergeFormProps {
  onBack: () => void;
}

const ConciergeForm: React.FC<ConciergeFormProps> = ({ onBack }) => {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    const patientName = formData.get('patientName');
    const contactNumber = formData.get('contactNumber');
    const address = formData.get('address');
    const gatedStatus = formData.get('gated');
    const landmark = formData.get('landmark');
    const requirements = formData.get('requirements');

    const subject = `New Concierge Booking Request: ${patientName}`;
    const body = `
Booking Details:
------------------
Patient Name: ${patientName}
Contact Number: ${contactNumber}
Residential Address: ${address}
Gated Community: ${gatedStatus}
Landmark: ${landmark}
Requirements: ${requirements}

Request generated from Whitefield Health Portal.
    `.trim();

    // Trigger mailto link
    const mailtoUrl = `mailto:whitefield.health.in@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;

    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 text-center py-20">
        <div className="w-24 h-24 bg-[#1CB496]/10 text-[#1CB496] rounded-full flex items-center justify-center mx-auto mb-8 border border-[#1CB496]/20 shadow-xl shadow-[#1CB496]/10">
          <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
        </div>
        <h2 className="text-4xl font-bold text-slate-900 mb-4">Request Confirmed</h2>
        <p className="text-slate-600 mb-10 text-lg font-medium">
          Your details have been shared with our clinical team. A dedicated Care Manager will call you within 15 minutes.
        </p>
        <button 
          onClick={onBack}
          className="bg-[#2E4A96] text-white px-10 py-4 rounded-2xl font-bold hover:bg-[#1e3266] transition-all shadow-xl shadow-blue-900/20"
        >
          Return to Home
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4">
      <div className="bg-white rounded-[2.5rem] shadow-2xl p-10 md:p-14 border border-slate-100">
        <div className="mb-12 text-center">
          <div className="flex justify-center mb-6">
             <div className="w-8 h-12 bg-[#2E4A96] rounded-sm"></div>
             <div className="w-8 h-12 bg-[#1CB496] rounded-sm ml-1"></div>
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-3">Concierge Booking</h2>
          <p className="text-slate-500 font-semibold tracking-wide">Customized medical support for Whitefield residents.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3">Patient's Full Name</label>
              <input name="patientName" required type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 focus:ring-2 focus:ring-[#1CB496] outline-none transition-all font-bold" placeholder="e.g. Anand Raghavan" />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3">Primary Contact</label>
              <input name="contactNumber" required type="tel" className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 focus:ring-2 focus:ring-[#1CB496] outline-none transition-all font-bold" placeholder="+91 99000 00000" />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3">Residential Address</label>
            <input name="address" required type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 focus:ring-2 focus:ring-[#1CB496] outline-none transition-all font-bold" placeholder="Apartment / Villa Name & Area" />
          </div>

          <div className="bg-[#2E4A96]/5 p-8 rounded-[2rem] border border-[#2E4A96]/10">
            <h4 className="text-xs font-black text-[#2E4A96] mb-6 flex items-center uppercase tracking-widest">
              <svg className="w-5 h-5 mr-3 text-[#1CB496]" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path></svg>
              Hyper-Local Verification
            </h4>
            <div className="space-y-6">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4">Gated Community Status</label>
                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center p-4 bg-white rounded-2xl border border-slate-100 cursor-pointer hover:border-[#1CB496] transition-all">
                    <input type="radio" name="gated" value="Yes, with Security" className="w-4 h-4 text-[#1CB496] focus:ring-[#1CB496]" defaultChecked />
                    <span className="ml-3 text-sm font-bold text-slate-700">Yes, with Security</span>
                  </label>
                  <label className="flex items-center p-4 bg-white rounded-2xl border border-slate-100 cursor-pointer hover:border-[#1CB496] transition-all">
                    <input type="radio" name="gated" value="Standalone Home" className="w-4 h-4 text-[#1CB496] focus:ring-[#1CB496]" />
                    <span className="ml-3 text-sm font-bold text-slate-700">Standalone Home</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3">Nearest Major Landmark</label>
                <select name="landmark" className="w-full bg-white border border-slate-200 rounded-2xl px-6 py-4 focus:ring-2 focus:ring-[#1CB496] outline-none transition-all text-sm font-bold text-slate-700 appearance-none">
                  <option value="Hope Farm Junction">Hope Farm Junction</option>
                  <option value="ITPL / GR Tech Park">ITPL / GR Tech Park</option>
                  <option value="Kadugodi Metro">Kadugodi Metro</option>
                  <option value="Varthur Kodi">Varthur Kodi</option>
                  <option value="Brookefield / AECS">Brookefield / AECS</option>
                </select>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3">Clinical Care Requirements</label>
            <textarea name="requirements" className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 focus:ring-2 focus:ring-[#1CB496] outline-none transition-all h-32 font-medium" placeholder="Briefly describe the medical history or current needs..."></textarea>
          </div>

          <div className="flex items-center justify-between pt-6">
            <button type="button" onClick={onBack} className="text-slate-400 font-bold hover:text-slate-800 transition-colors uppercase text-xs tracking-widest">Back</button>
            <button type="submit" className="bg-[#1CB496] hover:bg-[#158c75] text-white px-12 py-5 rounded-2xl font-black shadow-xl shadow-[#1CB496]/20 transition-all hover:scale-[1.02]">
              Review & Book Now
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConciergeForm;
